<script setup>
/**
 * FlexMessage Bubble Component
 * Displays LINE Flex Messages in the conversation view with full visual rendering
 */
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import BaseBubble from './Base.vue';
import FlexRenderer from './FlexRenderer.vue';
import FlexComponent from './FlexComponent.vue';
import { useMessageContext } from '../provider.js';

const { t } = useI18n();

// Get message context using the proper provider
const { contentAttributes, content } = useMessageContext();

// Toggle between preview and full render
const showFullRender = ref(true);

// Get flex message data from content_attributes (camelCase due to useCamelCase transform)
const flexMessage = computed(() => {
  return contentAttributes.value?.flexMessage || null;
});

const flexContents = computed(() => {
  // Support both Outgoing and Incoming Flex Message formats:
  // Outgoing: flexMessage = { type: 'flex', altText: '...', contents: { type: 'bubble', ... } }
  // Incoming: flexMessage = { type: 'bubble', ... } (contents directly, no wrapper)

  if (flexMessage.value?.contents) {
    // Outgoing format - has .contents wrapper
    return flexMessage.value.contents;
  }

  // Incoming format - flexMessage itself is the contents
  if (
    flexMessage.value?.type === 'bubble' ||
    flexMessage.value?.type === 'carousel'
  ) {
    return flexMessage.value;
  }

  return null;
});

const templateName = computed(() => {
  // Extract name from content like "[Flex] TEST" → "TEST"
  const match = content.value?.match(/^\[Flex\]\s*(.*)$/);
  return match ? match[1] : content.value || 'Flex Message';
});

const altText = computed(() => {
  // Support both formats:
  // Outgoing: flexMessage.altText
  // Incoming: contentAttributes.altText (stored separately)
  return (
    flexMessage.value?.altText ||
    contentAttributes.value?.altText ||
    templateName.value
  );
});

const hasValidFlexContent = computed(() => {
  return (
    flexContents.value &&
    (flexContents.value.type === 'bubble' ||
      flexContents.value.type === 'carousel')
  );
});
</script>

<script>
export default {
  name: 'FlexMessage',
  components: {
    FlexRenderer,
    FlexComponent,
  },
};
</script>

<template>
  <!-- Clean Flex Message - displays only the Flex content without wrapper -->
  <div class="flex-message-clean">
    <!-- Full Flex Render -->
    <FlexRenderer v-if="hasValidFlexContent" :flex-json="flexContents" />

    <!-- Fallback: Show message info if no valid flex content -->
    <BaseBubble v-else class="flex-fallback-bubble">
      <div class="flex-fallback">
        <div
          class="flex items-center gap-2 text-green-600 dark:text-green-400 mb-2"
        >
          <svg
            class="w-4 h-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <line x1="3" y1="9" x2="21" y2="9" />
            <line x1="9" y1="21" x2="9" y2="9" />
          </svg>
          <span class="font-semibold text-xs">Flex Message</span>
        </div>
        <div class="text-sm font-medium text-n-slate-12">
          {{ templateName }}
        </div>
        <div class="text-xs text-n-slate-11 mt-1">
          {{ altText }}
        </div>
      </div>
    </BaseBubble>
  </div>
</template>

<style scoped>
.flex-message-clean {
  /* Clean container - no extra styling, just the Flex content */
}

.flex-fallback-bubble {
  background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%);
}

:global(.dark) .flex-fallback-bubble {
  background: linear-gradient(
    135deg,
    rgba(34, 197, 94, 0.1) 0%,
    rgba(16, 185, 129, 0.1) 100%
  );
}

.flex-fallback {
  padding: 12px;
}
</style>
